import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { employerAPI } from '../../services/api';
import { toast } from 'sonner';
import { User, ChevronRight, Loader2, MessageSquare, Mail, Archive, Inbox, MailOpen, Trash2 } from 'lucide-react';

const Messages = ({ isFounderMode = false }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    try {
      const res = await employerAPI.getMessages();
      setMessages(res.data || []);
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleArchive = async (messageId, e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await employerAPI.archiveMessage(messageId);
      setMessages(prev => prev.map(m => 
        m.id === messageId ? { ...m, archived: true } : m
      ));
      toast.success('Message archived');
    } catch (error) {
      toast.error('Failed to archive message');
    }
  };

  const handleUnarchive = async (messageId, e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await employerAPI.unarchiveMessage(messageId);
      setMessages(prev => prev.map(m => 
        m.id === messageId ? { ...m, archived: false } : m
      ));
      toast.success('Message restored');
    } catch (error) {
      toast.error('Failed to restore message');
    }
  };

  const filteredMessages = messages.filter(msg => {
    if (activeTab === 'unread') return !msg.read && !msg.archived;
    if (activeTab === 'archived') return msg.archived;
    return !msg.archived; // 'all' shows non-archived
  });

  const unreadCount = messages.filter(m => !m.read && !m.archived).length;
  const archivedCount = messages.filter(m => m.archived).length;

  if (loading) {
    const content = (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-[#1B5AFF]" />
      </div>
    );
    return isFounderMode ? content : <DashboardLayout>{content}</DashboardLayout>;
  }

  const messagesContent = (
    <div className="space-y-6" data-testid="employer-messages">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-[#0A0F1E]">Messages</h1>
            <p className="mt-1 text-[#5B6472] text-sm">Conversations with candidates.</p>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-[#F5F8FF]/50 border border-[#E8EDF5] p-1 rounded-xl">
            <TabsTrigger 
              value="all" 
              className="data-[state=active]:bg-[#1B5AFF] data-[state=active]:text-white text-[#5B6472] hover:text-[#0A0F1E] rounded-lg px-4 py-2"
            >
              <Inbox className="w-4 h-4 mr-2" />
              All
            </TabsTrigger>
            <TabsTrigger 
              value="unread"
              className="data-[state=active]:bg-[#1B5AFF] data-[state=active]:text-white text-[#5B6472] hover:text-[#0A0F1E] rounded-lg px-4 py-2"
            >
              <MailOpen className="w-4 h-4 mr-2" />
              Unread
              {unreadCount > 0 && (
                <span className="ml-2 px-2 py-0.5 rounded-full bg-[#1B5AFF]/20 text-[#1B5AFF] text-xs">
                  {unreadCount}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger 
              value="archived"
              className="data-[state=active]:bg-[#1B5AFF] data-[state=active]:text-white text-[#5B6472] hover:text-[#0A0F1E] rounded-lg px-4 py-2"
            >
              <Archive className="w-4 h-4 mr-2" />
              Archived
              {archivedCount > 0 && (
                <span className="ml-2 px-2 py-0.5 rounded-full bg-[#5B6472]/20 text-[#5B6472] text-xs">
                  {archivedCount}
                </span>
              )}
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Messages List */}
        <div className="space-y-3">
          {filteredMessages.length === 0 ? (
            <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-8 text-center">
              {activeTab === 'archived' ? (
                <>
                  <Archive className="w-12 h-12 text-[#5B6472] mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-[#0A0F1E] mb-2">No archived messages</h3>
                  <p className="text-[#5B6472]">Archived messages will appear here.</p>
                </>
              ) : activeTab === 'unread' ? (
                <>
                  <MailOpen className="w-12 h-12 text-[#5B6472] mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-[#0A0F1E] mb-2">All caught up!</h3>
                  <p className="text-[#5B6472]">No unread messages.</p>
                </>
              ) : (
                <>
                  <Mail className="w-12 h-12 text-[#5B6472] mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-[#0A0F1E] mb-2">No messages yet</h3>
                  <p className="text-[#5B6472]">When you reach out to candidates, your conversations will appear here.</p>
                </>
              )}
            </Card>
          ) : (
            filteredMessages.map((msg, i) => (
              <Link
                key={msg.id}
                to={`/employer/messages/${msg.id}`}
                className="block"
              >
                <Card 
                  className={`bg-[#FFFFFF] border rounded-xl p-4 transition-all hover:border-[#1B5AFF]/50 animate-fade-in ${
                    !msg.read ? 'border-[#1B5AFF]/30' : 'border-[#E8EDF5]'
                  }`}
                  style={{ animationDelay: `${i * 0.03}s` }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      {msg.participant?.photo_url ? (
                        <img 
                          src={msg.participant.photo_url} 
                          alt="" 
                          className="w-12 h-12 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-[#F5F8FF] flex items-center justify-center">
                          <User className="w-5 h-5 text-[#5B6472]" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h3 className={`font-medium truncate ${!msg.read ? 'text-[#0A0F1E]' : 'text-[#0A0F1E]'}`}>
                            {msg.participant?.name || 'Unknown'}
                          </h3>
                          {!msg.read && (
                            <span className="w-2 h-2 rounded-full bg-[#1B5AFF]" />
                          )}
                        </div>
                        <p className="text-[#5B6472] text-sm truncate">{msg.last_message}</p>
                        <p className="text-[#5B6472] text-xs mt-1">
                          {new Date(msg.updated_at || msg.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => msg.archived ? handleUnarchive(msg.id, e) : handleArchive(msg.id, e)}
                        className="h-8 w-8 p-0 text-[#5B6472] hover:text-[#0A0F1E] hover:bg-[#F5F8FF]"
                        title={msg.archived ? 'Restore' : 'Archive'}
                      >
                        {msg.archived ? <Inbox className="w-4 h-4" /> : <Archive className="w-4 h-4" />}
                      </Button>
                      <ChevronRight className="w-5 h-5 text-[#5B6472]" />
                    </div>
                  </div>
                </Card>
              </Link>
            ))
          )}
        </div>
      </div>
  );

  return isFounderMode ? messagesContent : <DashboardLayout>{messagesContent}</DashboardLayout>;
};

export default Messages;
